<template>
  <li class="GoodsItem" @click="navTo('/mall/goods/' + id)">
    <div class="card-inner">
      <!-- 商品缩略图容器 -->
      <div class="img-box">
        <img
          v-if="img"
          :src="img"
          :alt="name || '商品图片'"
          class="goods-img"
        />
        <div v-else class="img-placeholder">
          <i class="el-icon-picture-outline"></i>
        </div>
        <!-- 标段标签小胶囊 (如果有标段数据) -->
        <span class="section-badge" v-if="sectionText">
          {{ sectionText }}
        </span>
      </div>

      <!-- 商品文本信息 -->
      <div class="goods-info">
        <h3 class="goods-title ellipsis-two" :title="name">
          {{ name }}
        </h3>

        <!-- SKU 编号与供应商 -->
        <div class="meta-row" v-if="sku || supplierName">
          <span class="sku-text" v-if="sku">{{ sku }}</span>
          <span class="supplier-text" v-if="supplierName">{{ supplierName }}</span>
        </div>

        <!-- 价格与协议说明 -->
        <div class="price-row">
          <div class="price-wrap">
            <span class="currency">¥</span>
            <span class="price-num tabular-nums">{{ formattedPrice }}</span>
          </div>
          <span class="tax-tag">含税协议价</span>
        </div>
      </div>
    </div>
  </li>
</template>

<script>
export default {
  name: "GoodsItem",
  props: {
    id: {
      type: [Number, String],
      default: () => "",
    },
    img: {
      type: String,
      default: () => "",
    },
    name: {
      type: String,
      default: () => "",
    },
    price: {
      type: [Number, String],
      default: () => "",
    },
    section: {
      type: [Number, String, Array],
      default: () => "",
    },
    supplierName: {
      type: String,
      default: () => "",
    },
    sku: {
      type: [Number, String, Array],
      default: () => "",
    },
  },
  computed: {
    sectionText () {
      if (!this.section) return "";
      if (Array.isArray(this.section)) {
        return this.section.map((el) => el.value || el).filter(Boolean).join("-");
      }
      return String(this.section);
    },
    formattedPrice () {
      if (this.price === null || this.price === undefined || this.price === "") {
        return "0.00";
      }
      const num = Number(this.price);
      return Number.isFinite(num) ? num.toFixed(2) : String(this.price);
    },
  },
  methods: {
    navTo (route) {
      this.$router.push(route);
    },
  },
};
</script>

<style scoped lang="less">
@import '../assets/css/var.less';

.GoodsItem {
  list-style: none;
  background-color: #ffffff;
  border: 1px solid @brandBorder;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  &:hover {
    border-color: rgba(15, 82, 186, 0.35);
    box-shadow: 0 6px 20px rgba(0, 60, 144, 0.08);
    transform: translateY(-2px);

    .img-box .goods-img {
      transform: scale(1.04);
    }

    .goods-info .goods-title {
      color: @brandContainer;
    }
  }

  .card-inner {
    padding: 12px;
    display: flex;
    flex-direction: column;
    height: 100%;
    box-sizing: border-box;
  }

  .img-box {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    height: 190px;
    background-color: #f8fafc;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px;
    box-sizing: border-box;

    .goods-img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      mix-blend-mode: multiply;
      transition: transform 0.3s ease;
    }

    .img-placeholder {
      font-size: 32px;
      color: @brandBorder;
    }

    .section-badge {
      position: absolute;
      top: 8px;
      left: 8px;
      font-size: 11px;
      font-weight: 500;
      color: @brandPrimary;
      background-color: rgba(255, 255, 255, 0.95);
      border: 1px solid rgba(15, 82, 186, 0.2);
      padding: 1px 6px;
      border-radius: 4px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      max-width: 80%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .goods-info {
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: space-between;

    .goods-title {
      font-size: 13px;
      font-weight: 500;
      color: @brandTextDark;
      line-height: 1.45;
      margin: 0;
      transition: color 0.15s ease;
      min-height: 38px;
    }

    .meta-row {
      margin-top: 6px;
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 11px;
      color: @brandTextLight;
      overflow: hidden;

      .sku-text {
        font-family: inherit;
        background-color: #f1f5f9;
        padding: 1px 4px;
        border-radius: 3px;
        color: @brandTextMuted;
        font-size: 10px;
        letter-spacing: 0.02em;
      }

      .supplier-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .price-row {
      margin-top: 10px;
      padding-top: 8px;
      border-top: 1px solid @brandBorderLight;
      display: flex;
      align-items: baseline;
      justify-content: space-between;

      .price-wrap {
        display: flex;
        align-items: baseline;
        color: @brandPrimary;

        .currency {
          font-size: 12px;
          font-weight: 600;
          margin-right: 2px;
        }

        .price-num {
          font-size: 18px;
          font-weight: 700;
          line-height: 1;
        }
      }

      .tax-tag {
        font-size: 10px;
        color: @brandTextLight;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/GoodsItem.vue